<template>
  <div class="chatbot-message d-flex flex-column" :class="message.role === 'user' ? 'align-end' : 'align-start'">
    <div
      class="chatbot-bubble rounded-lg pa-3"
      :class="message.role === 'user' ? 'bg-tertiary-500' : 'bg-primary-500'"
      :style="message.isError ? 'border-left: 3px solid #FF2E2E' : ''"
      @click.capture="handleClick"
      @mouseover="handleHover"
      @mouseleave="hoveredChip = null"
    >
      <span v-if="message.isError" class="chatbot-bubble-text text-accent-900">{{ errorText }}</span>
      <!-- eslint-disable-next-line vue/no-v-html -->
      <div v-else class="chatbot-bubble-text" v-html="renderedHtml" />

      <ChatbotMapAsset v-for="(asset, index) in mapAssets" :key="`map-${index}`" :points="asset.points" :title="asset.title" />

      <div v-for="(asset, index) in imageAssets" :key="`image-${index}`" class="mt-2">
        <p v-if="asset.title" class="text-caption text-primary-300 mb-1 text-center">{{ asset.title }}</p>
        <div
          class="chatbot-image-wrapper rounded-lg"
          @click="$emit('openLightbox', imageSrc(asset))"
        >
          <img
            :src="imageSrc(asset)"
            class="chatbot-image-asset rounded-lg"
          />
          <div class="chatbot-image-overlay rounded-lg">
            <div class="chatbot-image-zoom-btn">
              <v-icon size="24" color="white">mdi-magnify-plus-outline</v-icon>
            </div>
          </div>
        </div>
      </div>

    </div>

    <v-tooltip
      v-if="hoveredChip"
      :key="hoveredSn"
      :activator="hoveredChip"
      :model-value="true"
      location="top"
      content-class="entity-chip-tooltip"
    >
      <div class="d-flex align-center">
        <v-icon size="12" class="mr-1" color="tertiary-900">mdi-content-copy</v-icon>
        <span>{{ tooltipLabel }}</span>
        <span v-if="showTooltipSn" class="entity-chip-tooltip-sn ml-1">{{ hoveredSn }}</span>
      </div>
    </v-tooltip>

    <div v-if="message.role === 'bot' && message.runId" class="d-flex align-center mt-1 chatbot-feedback">
      <v-btn
        icon
        size="x-small"
        variant="text"
        :color="message.feedbackScore === 'up' ? 'tertiary-900' : 'primary-300'"
        @click="handleThumbUp"
      >
        <v-icon size="16">mdi-thumb-up</v-icon>
      </v-btn>
      <v-btn
        icon
        size="x-small"
        variant="text"
        :color="message.feedbackScore === 'down' ? 'accent-900' : 'primary-300'"
        @click="handleThumbDown"
      >
        <v-icon size="16">mdi-thumb-down</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import ChatbotMapAsset from "@/components/molecules/chatbot/ChatbotMapAsset.vue";
import { renderChatbotMessage } from "@/helpers/markdown";
import { ChatbotImageAsset, ChatbotMapAsset as ChatbotMapAssetModel, ChatbotMessage } from "@/services/chatbot-service/models";
import { useChatbotStore } from "@/store/chatbot";
import { ToastType, useToastStore } from "@/store/toast";

const { t } = useI18n();
const router = useRouter();
const store = useChatbotStore();
const toasts = useToastStore();

const props = defineProps<{ message: ChatbotMessage }>();
const emit = defineEmits<{
  thumbsUp: [messageId: string];
  thumbsDown: [messageId: string];
  openLightbox: [src: string];
}>();

const mapAssets = computed(() => (props.message.assets ?? []).filter((a): a is ChatbotMapAssetModel => a.kind === "map"));
const imageAssets = computed(() => (props.message.assets ?? []).filter((a): a is ChatbotImageAsset => a.kind === "image"));

function imageSrc(asset: ChatbotImageAsset): string {
  return `data:${asset.mime ?? 'image/png'};base64,${asset.base64}`
}

const renderedHtml = computed(() => renderChatbotMessage(props.message.content, store.entities));

// Custom tooltip on vehicle chips: tracks the hovered chip element and feeds a
// v-tooltip anchored to it (the chips live in v-html, so no template binding).
const hoveredChip = ref<HTMLElement | null>(null);
const hoveredSn = computed(() => hoveredChip.value?.dataset.sn ?? "");
const hoveredName = computed(() => hoveredChip.value?.textContent?.trim() ?? "");
// When the entity name is unknown the chip already displays the SN — skip the
// redundant "of <SN>… <SN>" form.
const showTooltipSn = computed(() => hoveredName.value !== "" && hoveredName.value !== hoveredSn.value);
const tooltipLabel = computed(() =>
  showTooltipSn.value ? t("chatbot.copy-sn-tooltip-of", { name: hoveredName.value }) : t("chatbot.copy-sn-tooltip"),
);

function handleHover(event: MouseEvent) {
  hoveredChip.value = (event.target as HTMLElement).closest(".entity-chip[data-sn]") as HTMLElement | null;
}

// Error bubbles show a specific localized message for known API error codes
// (budget_exceeded / rate_limited / auth_error / upstream_error), falling back
// to the generic one.
const errorText = computed(() => {
  const keyByCode: Record<string, string> = {
    budget_exceeded: "chatbot.error-budget-exceeded",
    rate_limited: "chatbot.error-rate-limited",
    auth_error: "chatbot.error-auth",
    upstream_error: "chatbot.error-upstream",
  };
  const code = props.message.errorCode;
  return t((code && keyByCode[code]) || "chatbot.error");
});

function handleClick(event: MouseEvent) {
  const target = event.target as HTMLElement;

  const chip = target.closest(".entity-chip[data-sn]") as HTMLElement | null;
  if (chip) {
    copySn(chip.dataset.sn!);
    return;
  }

  const anchor = target.closest("a[data-internal]") as HTMLAnchorElement | null;
  if (!anchor) return;
  event.preventDefault();
  router.push(anchor.getAttribute("data-internal")!);
}

async function copySn(sn: string) {
  try {
    await navigator.clipboard.writeText(sn);
    toasts.addToast(t("chatbot.copy-sn-success"), ToastType.SUCCESS);
  } catch {
    toasts.addToast(t("chatbot.copy-sn-error"), ToastType.ERROR);
  }
}

function handleThumbUp() {
  emit("thumbsUp", props.message.id);
}

function handleThumbDown() {
  emit("thumbsDown", props.message.id);
}
</script>

<style scoped>
.chatbot-message {
  max-width: 100%;
}

.chatbot-bubble {
  max-width: 85%;
  word-break: break-word;
  color: white;
}

.chatbot-bubble-text :deep(p) {
  margin: 0 0 0.5em;
}

.chatbot-bubble-text :deep(p:last-child) {
  margin-bottom: 0;
}

.chatbot-bubble-text :deep(ul),
.chatbot-bubble-text :deep(ol) {
  margin: 0.25em 0 0.5em;
  padding-left: 1.25em;
}

.chatbot-bubble-text :deep(li) {
  margin-bottom: 0.15em;
}

.chatbot-bubble-text :deep(strong) {
  font-weight: 700;
}

.chatbot-bubble-text :deep(a) {
  color: #a1a2a8;
  text-decoration: underline;
}

.chatbot-bubble-text :deep(a:hover) {
  color: #ffffff;
}

/* Mirrors v-chip size="x-small" variant="tonal" color="tertiary-900" */
.chatbot-bubble-text :deep(.entity-chip) {
  display: inline-block;
  padding: 0 8px;
  max-width: 160px;
  height: 20px;
  line-height: 20px;
  border-radius: 10px;
  font-size: 0.6875rem;
  font-weight: 600;
  background-color: rgba(255, 241, 0, 0.12);
  color: #fff100;
  vertical-align: middle;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chatbot-bubble-text :deep(.entity-chip[data-sn]) {
  cursor: pointer;
}

.chatbot-image-wrapper {
  position: relative;
  display: block;
  cursor: zoom-in;
  width: 100%;
}

.chatbot-image-asset {
  display: block;
  width: 100%;
  height: auto;
}

.chatbot-image-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  padding: 8px;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.chatbot-image-wrapper:hover .chatbot-image-overlay {
  opacity: 1;
}

.chatbot-image-zoom-btn {
  background: rgba(0, 0, 0, 0.5);
  border-radius: 8px;
  padding: 8px;
  display: flex;
  align-items: center;
}

.entity-chip-tooltip-sn {
  font-family: monospace;
  font-size: 0.6875rem;
  font-weight: 600;
  color: #fff100;
  background-color: rgba(255, 241, 0, 0.12);
  border-radius: 6px;
  padding: 0 6px;
}
</style>

<!-- Unscoped: the tooltip content is teleported into the Vuetify overlay
     container, out of reach of scoped selectors. -->
<style>
.entity-chip-tooltip.v-overlay__content {
  background-color: #2c374c; /* primary-350 */
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 5px 10px;
  font-size: 0.75rem;
  box-shadow: 0 4px 12px rgba(2, 6, 20, 0.5);
}
</style>
